/**
 * F606SubscriptionsAdmin — subscription lifecycle actions.
 */
import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { RefreshCw, Loader2, Clock, XCircle, ArrowUpCircle } from 'lucide-react';
import { bridge, describeActions } from './bridgeClient';
import { DirhamPrice } from '../DirhamPrice';

export default function F606SubscriptionsAdmin({ language }) {
  const isAr = language === 'ar';
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    const res = await bridge.list('subscriptions', { limit: 100 });
    if (res.success !== false) setRows(res.data || []);
    else toast.error(res.error?.message || 'Failed to load subscriptions');
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (sub, fn, label, body = {}) => {
    setBusy((s) => ({ ...s, [sub.id]: label }));
    const res = await fn(sub.id, body);
    setBusy((s) => ({ ...s, [sub.id]: null }));
    if (res.success) {
      toast.success(`✅ ${label} · ${describeActions(res.actions_fired, language)}`, { duration: 5000 });
      load();
    } else {
      toast.error(`⚠️ ${res.error?.message || 'Failed'}`);
    }
  };

  return (
    <div className="space-y-3" data-testid="f606-subs-admin">
      <div className="flex justify-between items-center">
        <div className="text-white/60 text-xs">{rows.length} {isAr ? 'اشتراك' : 'subscriptions'}</div>
        <button onClick={load} className="p-2 rounded-lg bg-white/5 border border-white/10 text-white/70 hover:bg-white/10">
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="rounded-2xl border border-white/10 bg-black/20 overflow-hidden">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-white/5 text-white/50 text-[10px] uppercase tracking-wider">
              <th className="px-3 py-2 text-start">{isAr ? 'المستخدم' : 'User'}</th>
              <th className="px-3 py-2 text-start">{isAr ? 'الخطّة' : 'Plan'}</th>
              <th className="px-3 py-2 text-end">{isAr ? 'المبلغ' : 'Amount'}</th>
              <th className="px-3 py-2 text-center">{isAr ? 'الحالة' : 'Status'}</th>
              <th className="px-3 py-2 text-end">{isAr ? 'ينتهي' : 'Expires'}</th>
              <th className="px-3 py-2 text-end">{isAr ? 'الإجراءات' : 'Actions'}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {loading && rows.length === 0 && (
              <tr><td colSpan="6" className="text-center py-8"><Loader2 className="h-5 w-5 animate-spin inline text-emerald-400/50" /></td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan="6" className="text-center py-8 text-white/40">{isAr ? 'لا توجد اشتراكات' : 'No subscriptions'}</td></tr>
            )}
            {rows.map((s) => {
              const bId = busy[s.id];
              const status = String(s.status || s.payment_status || '').toLowerCase();
              const active = !['cancelled', 'expired', 'refunded'].includes(status);
              return (
                <tr key={s.id} className="hover:bg-white/[0.02]" data-testid={`f606-sub-row-${s.id}`}>
                  <td className="px-3 py-2 text-white font-mono text-[11px]" dir="ltr">{s.user_name || (s.user_id ? s.user_id.slice(0, 8) : '—')}</td>
                  <td className="px-3 py-2 text-white/70 font-mono text-[11px]" dir="ltr">{s.plan_name || s.trainer_code || (s.program_id ? s.program_id.slice(0, 8) : '—')}</td>
                  <td className="px-3 py-2 text-end font-mono text-emerald-300" dir="ltr">
                    <DirhamPrice value={(s.amount ?? s.price_paid_aed) ?? 0} decimals={2} size="sm" />
                  </td>
                  <td className="px-3 py-2 text-center">
                    <span className={`inline-flex px-2 py-0.5 rounded-md text-[10px] font-bold border ${
                      active ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300' : 'bg-neutral-500/15 border-neutral-500/40 text-neutral-300'
                    }`}>
                      {status || '—'}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-end text-white/60 font-mono text-[11px]" dir="ltr">
                    {s.expires_at ? String(s.expires_at).slice(0, 10) : '—'}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex gap-1 justify-end flex-wrap">
                      {active && (
                        <button
                          disabled={bId}
                          onClick={() => {
                            const days = Number(window.prompt(isAr ? 'أيّام إضافيّة:' : 'Additional days:') || '30');
                            if (!days) return;
                            act(s, bridge.subscription.extend, isAr ? 'تمديد' : 'Extend', { additional_days: days });
                          }}
                          className="px-2 py-1 rounded-md bg-cyan-500/15 border border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/25 text-[10px] font-bold disabled:opacity-40"
                          data-testid={`f606-sub-extend-${s.id}`}
                        >
                          <Clock className="h-3 w-3 inline" /> {isAr ? 'تمديد' : 'Extend'}
                        </button>
                      )}
                      <button
                        disabled={bId}
                        onClick={() => {
                          const plan = window.prompt(isAr ? 'الخطّة الجديدة:' : 'New plan:');
                          if (!plan) return;
                          act(s, bridge.subscription.upgrade, isAr ? 'ترقية' : 'Upgrade', { new_plan: plan, prorate: true });
                        }}
                        className="px-2 py-1 rounded-md bg-purple-500/15 border border-purple-500/40 text-purple-300 hover:bg-purple-500/25 text-[10px] font-bold disabled:opacity-40"
                        data-testid={`f606-sub-upgrade-${s.id}`}
                      >
                        <ArrowUpCircle className="h-3 w-3 inline" /> {isAr ? 'ترقية' : 'Upgrade'}
                      </button>
                      {active && (
                        <button
                          disabled={bId}
                          onClick={() => {
                            if (!window.confirm(isAr ? 'إلغاء الاشتراك؟' : 'Cancel subscription?')) return;
                            const refund = Number(window.prompt(isAr ? 'مبلغ الاسترداد ($) (0 = بدون):' : 'Refund amount ($) (0 = none):') || '0');
                            act(s, bridge.subscription.cancel, isAr ? 'إلغاء' : 'Cancel', {
                              immediate: true, refund_amount: refund,
                            });
                          }}
                          className="px-2 py-1 rounded-md bg-red-500/10 border border-red-500/40 text-red-300 hover:bg-red-500/20 text-[10px] font-bold disabled:opacity-40"
                          data-testid={`f606-sub-cancel-${s.id}`}
                        >
                          <XCircle className="h-3 w-3 inline" /> {isAr ? 'إلغاء' : 'Cancel'}
                        </button>
                      )}
                      {bId && <Loader2 className="h-3 w-3 animate-spin text-emerald-400/70" />}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
